import { useEffect } from 'react';
import { useApplicationAction } from '@/hooks/useApplicationAction';
import { useJobs } from '@/hooks/useJobs';
import { useJobBrowserStoreApi } from '@/providers/dashboard/JobBrowserContext';
import type { Density, ShellView } from '@/providers/dashboard/ShellContext';
import {
  useDisplayDensity,
  useOverlays,
  useShellNav,
} from '@/providers/dashboard/ShellContext';

interface KeyboardShortcutDeps {
  addUrlOpen: boolean;
  closePalette: () => void;
  density: Density;
  onAction: (jobId: number, action: 'approve' | 'reject') => void;
  openAddUrl: () => void;
  openPalette: () => void;
  paletteOpen: boolean;
  select: (id: number | null) => void;
  selectedId: number | null;
  setDensity: (density: Density) => void;
  setView: (view: ShellView) => void;
  toggleRail: () => void;
  visibleJobIds: number[];
}

const handleModifierKey = (e: KeyboardEvent, deps: KeyboardShortcutDeps) => {
  if (!(e.metaKey || e.ctrlKey)) {
    return false;
  }

  const key = e.key.toLowerCase();

  if (key === 'k') {
    e.preventDefault();
    if (deps.paletteOpen) {
      deps.closePalette();
    } else {
      deps.openPalette();
    }
    return true;
  }

  if (key === 'b') {
    e.preventDefault();
    deps.toggleRail();
    return true;
  }

  return false;
};

const handleEscape = (e: KeyboardEvent, deps: KeyboardShortcutDeps) => {
  if (e.key !== 'Escape') {
    return false;
  }

  if (!deps.paletteOpen && !deps.addUrlOpen && deps.selectedId != null) {
    deps.select(null);
  }

  return true;
};

const handleJobCycle = (e: KeyboardEvent, deps: KeyboardShortcutDeps) => {
  if (e.key !== 'j' && e.key !== 'k') {
    return false;
  }

  const list = deps.visibleJobIds;
  const [first] = list;

  if (first == null) {
    return true;
  }

  const i = deps.selectedId != null ? list.indexOf(deps.selectedId) : -1;

  if (e.key === 'j') {
    deps.select(list[Math.min(i + 1, list.length - 1)] ?? first);
  } else {
    deps.select(list[Math.max(0, i - 1)] ?? first);
  }

  return true;
};

const handleJobAction = (e: KeyboardEvent, deps: KeyboardShortcutDeps) => {
  if (deps.selectedId == null) {
    return false;
  }

  if (e.key === 'a') {
    deps.onAction(deps.selectedId, 'approve');
    return true;
  }

  if (e.key === 'x') {
    deps.onAction(deps.selectedId, 'reject');
    return true;
  }

  return false;
};

const isEditable = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false;
  }
  if (['INPUT', 'TEXTAREA'].includes(target.tagName)) {
    return true;
  }
  return target.isContentEditable;
};

const GOTO_CHORD_TIMEOUT_MS = 1500;

const handleGoto = (e: KeyboardEvent, deps: KeyboardShortcutDeps) => {
  if (e.key !== 'g') {
    return false;
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), GOTO_CHORD_TIMEOUT_MS);

  window.addEventListener(
    'keydown',
    (ev) => {
      // Ignore the chord's second key when it lands in an editable field —
      // the user is typing, not navigating.
      if (isEditable(ev.target)) {
        return;
      }

      clearTimeout(timeout);
      controller.abort();

      if (ev.key === 'f') {
        deps.setView('feed');
      } else if (ev.key === 't') {
        deps.setView('tracker');
      } else if (ev.key === 's') {
        deps.setView('settings');
      }
    },
    { signal: controller.signal },
  );

  return true;
};

const handleUnmodifiedKey = (e: KeyboardEvent, deps: KeyboardShortcutDeps) => {
  if (e.key === '/') {
    e.preventDefault();
    document
      .querySelector<HTMLInputElement>('[data-testid="job-search-input"]')
      ?.focus();
    return;
  }

  if (handleJobCycle(e, deps)) {
    return;
  }

  if (handleJobAction(e, deps)) {
    return;
  }

  if (e.key === 'd') {
    deps.setDensity(deps.density === 'compact' ? 'comfortable' : 'compact');
    return;
  }

  if (e.key === 'u') {
    deps.openAddUrl();
    return;
  }

  handleGoto(e, deps);
};

export const useKeyboardShortcuts = () => {
  const { setView } = useShellNav();
  const { density, setDensity } = useDisplayDensity();
  const { addUrlOpen, openAddUrl, openPalette, paletteOpen, closePalette } =
    useOverlays();
  const store = useJobBrowserStoreApi();
  const jobs = useJobs();
  const applicationAction = useApplicationAction();

  useEffect(() => {
    const onAction = (jobId: number, action: 'approve' | 'reject') => {
      const { status, applicationId } =
        jobs.find(({ id }) => id === jobId) ?? {};

      if (status !== 'ready' || !applicationId) {
        return;
      }

      applicationAction.mutate({ action, applicationId });
    };

    const onKey = (e: KeyboardEvent) => {
      // Read high-frequency state imperatively at press time — avoids
      // re-registering the listener on every selection / visible-list change.
      const { selectedId, visibleJobIds, select, toggleRail } =
        store.getState();

      const deps: KeyboardShortcutDeps = {
        addUrlOpen,
        closePalette,
        density,
        onAction,
        openAddUrl,
        openPalette,
        paletteOpen,
        select,
        selectedId,
        setDensity,
        setView,
        toggleRail,
        visibleJobIds,
      };

      if (handleModifierKey(e, deps)) {
        return;
      }

      if (handleEscape(e, deps)) {
        return;
      }

      if (isEditable(e.target)) {
        return;
      }

      handleUnmodifiedKey(e, deps);
    };

    window.addEventListener('keydown', onKey);

    return () => window.removeEventListener('keydown', onKey);
  }, [
    addUrlOpen,
    applicationAction,
    closePalette,
    density,
    jobs,
    openAddUrl,
    openPalette,
    paletteOpen,
    setDensity,
    setView,
    store,
  ]);
};
